import React from 'react'
import { X, Download, Building2, User, Car } from 'lucide-react'
import {
 ShadcnDialog as Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 ShadcnButton,
 Tabs,
 TabsList,
 TabsTrigger,
 TabsContent,
} from '../ui'
import { FiscalDocument } from '../../modules/financial/FiscalDocumentsPage'
import { formatCurrency, formatDateToBR } from '../../utils/formatters'

interface FiscalDocumentDetailsCardProps {
 document: FiscalDocument
 onClose: () => void
 onDownloadXML: (documentId: string) => void
}

export const FiscalDocumentDetailsCard: React.FC<FiscalDocumentDetailsCardProps> = ({
 document,
 onClose,
 onDownloadXML,
}) => {
 const productValue = parseFloat(document.productValue.toString())
 const chargesValue = parseFloat(document.chargesValue.toString())
 const discountValue = parseFloat(document.discountValue.toString())
 const totalValue = parseFloat(document.totalValue.toString())
 const icmsBase = parseFloat(document.icmsBase.toString())
 const icmsValue = parseFloat(document.icmsValue.toString())
 const ipiValue = parseFloat(document.ipiValue.toString())
 const pisValue = parseFloat(document.pisValue.toString())
 const cofinsValue = parseFloat(document.cofinsValue.toString())

 return (
 <Dialog open={true} onOpenChange={onClose}>
 <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle className="flex items-center justify-between">
 <span>Detalhes do Documento Fiscal</span>
 <div className="flex gap-2">
 {document.xml && (
 <ShadcnButton
 variant="outline"
 size="sm"
 onClick={() => onDownloadXML(document.id)}
 >
 <Download className="h-4 w-4 mr-2"/>
 Baixar XML
 </ShadcnButton>
 )}
 <ShadcnButton variant="ghost"size="sm"onClick={onClose}>
 <X className="h-4 w-4"/>
 </ShadcnButton>
 </div>
 </DialogTitle>
 </DialogHeader>

 <Tabs defaultValue="general"className="w-full">
 <TabsList className="grid w-full grid-cols-4">
 <TabsTrigger value="general">Geral</TabsTrigger>
 <TabsTrigger value="emitter">Emitente</TabsTrigger>
 <TabsTrigger value="customer">Destinatário</TabsTrigger>
 <TabsTrigger value="product">Produto</TabsTrigger>
 </TabsList>

 <TabsContent value="general"className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base">Identificação</CardTitle>
 </CardHeader>
 <CardContent className="space-y-2">
 <div>
 <span className="text-sm text-muted-foreground">Número:</span>
 <p className="font-medium">{document.number || 'N/A'}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">Série:</span>
 <p className="font-medium">{document.series || 'N/A'}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">Chave de Acesso:</span>
 <p className="font-mono text-xs break-all">{document.accessKey || 'N/A'}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">Status:</span>
 <p className="font-medium">{document.status}</p>
 </div>
 {document.issueDate && (
 <div>
 <span className="text-sm text-muted-foreground">Data de Emissão:</span>
 <p className="font-medium">{formatDateToBR(document.issueDate)}</p>
 </div>
 )}
 {document.protocol && (
 <div>
 <span className="text-sm text-muted-foreground">Protocolo:</span>
 <p className="font-mono text-xs">{document.protocol}</p>
 </div>
 )}
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">Valores</CardTitle>
 </CardHeader>
 <CardContent className="space-y-2">
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">Valor do Produto:</span>
 <span className="font-medium">{formatCurrency(productValue)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">Encargos:</span>
 <span className="font-medium">{formatCurrency(chargesValue)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">Desconto:</span>
 <span className="font-medium text-red-600">
 -{formatCurrency(discountValue)}
 </span>
 </div>
 <div className="flex justify-between pt-2 border-t">
 <span className="font-semibold">Valor Total:</span>
 <span className="font-bold text-green-600">{formatCurrency(totalValue)}</span>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">Impostos</CardTitle>
 </CardHeader>
 <CardContent className="space-y-2">
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">Base ICMS:</span>
 <span className="font-medium">{formatCurrency(icmsBase)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">ICMS:</span>
 <span className="font-medium">{formatCurrency(icmsValue)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">IPI:</span>
 <span className="font-medium">{formatCurrency(ipiValue)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">PIS:</span>
 <span className="font-medium">{formatCurrency(pisValue)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-muted-foreground">COFINS:</span>
 <span className="font-medium">{formatCurrency(cofinsValue)}</span>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <CardTitle className="text-base">Financeiro</CardTitle>
 </CardHeader>
 <CardContent className="space-y-2">
 <div>
 <span className="text-sm text-muted-foreground">Tipo de Pagamento:</span>
 <p className="font-medium">
 {document.paymentType === 'cash' ? 'À Vista' : 'Financiamento'}
 </p>
 </div>
 {document.invoiceReference && (
 <div>
 <span className="text-sm text-muted-foreground">Referência da Fatura:</span>
 <p className="font-medium">{document.invoiceReference}</p>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </TabsContent>

 <TabsContent value="emitter"className="space-y-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center">
 <Building2 className="h-5 w-5 mr-2"/>
 Emitente (Concessionária)
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div>
 <span className="text-sm text-muted-foreground">Nome:</span>
 <p className="font-medium">{document.emitterName}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">CNPJ:</span>
 <p className="font-medium">{document.emitterCnpj}</p>
 </div>
 {document.emitterIe && (
 <div>
 <span className="text-sm text-muted-foreground">Inscrição Estadual:</span>
 <p className="font-medium">{document.emitterIe}</p>
 </div>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="customer"className="space-y-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center">
 <User className="h-5 w-5 mr-2"/>
 Destinatário (Cliente)
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div>
 <span className="text-sm text-muted-foreground">Nome:</span>
 <p className="font-medium">{document.customerName}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">Tipo:</span>
 <p className="font-medium">{document.customerType === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">
 {document.customerType === 'PF' ? 'CPF' : 'CNPJ'}:
 </span>
 <p className="font-medium">{document.customerDocument}</p>
 </div>
 {document.customerIe && (
 <div>
 <span className="text-sm text-muted-foreground">Inscrição Estadual:</span>
 <p className="font-medium">{document.customerIe}</p>
 </div>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 <TabsContent value="product"className="space-y-4">
 <Card>
 <CardHeader>
 <CardTitle className="text-base flex items-center">
 <Car className="h-5 w-5 mr-2"/>
 Produto (Veículo)
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-3">
 <div>
 <span className="text-sm text-muted-foreground">Descrição:</span>
 <p className="font-medium">{document.description}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">Ano do Modelo:</span>
 <p className="font-medium">{document.modelYear}</p>
 </div>
 {document.chassi && (
 <div>
 <span className="text-sm text-muted-foreground">Chassi:</span>
 <p className="font-mono text-xs">{document.chassi}</p>
 </div>
 )}
 {document.renavam && (
 <div>
 <span className="text-sm text-muted-foreground">RENAVAM:</span>
 <p className="font-mono text-xs">{document.renavam}</p>
 </div>
 )}
 <div>
 <span className="text-sm text-muted-foreground">CFOP:</span>
 <p className="font-medium">{document.cfop}</p>
 </div>
 <div>
 <span className="text-sm text-muted-foreground">Valor do Produto:</span>
 <p className="font-semibold text-lg">{formatCurrency(productValue)}</p>
 </div>
 </CardContent>
 </Card>
 </TabsContent>
 </Tabs>
 </DialogContent>
 </Dialog>
 )
}

